import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const getRoleLabel = (role?: string) => {
  if (role === 'owner') return 'Владелец';
  if (role === 'supervisor') return 'Руководитель';
  if (role === 'manager') return 'Менеджер';
  return role || '—';
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #f1f5f9',
  }}>
    <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: 14, color: '#0f172a', fontWeight: 500 }}>{value}</span>
  </div>
);

const Settings: React.FC = () => {
  const { user } = useAuth();

  const [sectionHovered, setSectionHovered] = useState<string | null>(null);

  const cardStyle = (key: string): React.CSSProperties => ({
    background: '#fff',
    borderRadius: 12,
    padding: '20px 24px',
    border: '1px solid #e2e8f0',
    boxShadow: sectionHovered === key ? '0 8px 25px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'box-shadow 0.2s ease',
  });

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={styles.title}>Настройки</h1>
        <p style={styles.subtitle}>Управление аккаунтом и параметрами системы</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 20, maxWidth: 720 }}>

        {/* Profile */}
        <section>
          <h3 style={styles.sectionLabel}>Профиль</h3>
          <div
            style={cardStyle('profile')}
            onMouseEnter={() => setSectionHovered('profile')}
            onMouseLeave={() => setSectionHovered(null)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <div style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
                color: '#fff',
                flexShrink: 0,
              }}>
                {user?.name
                  ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                  : '?'}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{user?.name || '—'}</div>
                <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{getRoleLabel(user?.role)}</div>
              </div>
            </div>
            <div>
              <InfoRow label="Email" value={user?.email || '—'} />
              <InfoRow label="Имя" value={user?.name || '—'} />
              <InfoRow label="Роль" value={getRoleLabel(user?.role)} />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 0',
              }}>
                <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>Компания</span>
                <span style={{ fontSize: 14, color: '#0f172a', fontWeight: 500 }}>{user?.company_name || '—'}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section>
          <h3 style={styles.sectionLabel}>Интеграции</h3>
          <div
            style={cardStyle('integrations')}
            onMouseEnter={() => setSectionHovered('integrations')}
            onMouseLeave={() => setSectionHovered(null)}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: '#f0fdf4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                }}>
                  🔗
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Битрикс24</div>
                  <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>CRM и телефония</div>
                </div>
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 12px',
                borderRadius: 20,
                background: '#dcfce7',
                color: '#16a34a',
              }}>
                Подключено
              </span>
            </div>
          </div>
        </section>

        {/* Checklist */}
        <section>
          <h3 style={styles.sectionLabel}>Чек-лист оценки</h3>
          <div
            style={cardStyle('checklist')}
            onMouseEnter={() => setSectionHovered('checklist')}
            onMouseLeave={() => setSectionHovered(null)}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                flexShrink: 0,
              }}>
                📋
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>
                  Сопровождение ВЭД
                </div>
                <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                  Текущий чек-лист настроен для оценки диалогов по продаже услуг ВЭД-сопровождения.
                  Включает критерии: выявление потребности, презентация услуг, работа с возражениями, закрытие сделки.
                </div>
                <div style={{
                  marginTop: 12,
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap' as const,
                }}>
                  {['Выявление потребности', 'Презентация', 'Возражения', 'Закрытие'].map((tag, i) => (
                    <span key={i} style={{
                      fontSize: 11,
                      fontWeight: 500,
                      padding: '3px 10px',
                      borderRadius: 20,
                      background: '#eff6ff',
                      color: '#3b82f6',
                    }}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section>
          <h3 style={styles.sectionLabel}>О системе</h3>
          <div
            style={cardStyle('about')}
            onMouseEnter={() => setSectionHovered('about')}
            onMouseLeave={() => setSectionHovered(null)}
          >
            <InfoRow label="Версия" value="1.0.0" />
            <InfoRow label="Сервер" value={process.env.REACT_APP_API_URL || '/api'} />
            <InfoRow label="AI модель" value="Claude (Anthropic)" />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 0',
            }}>
              <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>Статус</span>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '3px 10px',
                borderRadius: 20,
                background: '#dcfce7',
                color: '#16a34a',
              }}>
                Работает
              </span>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px 36px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  subtitle: {
    fontSize: 15,
    color: '#94a3b8',
    margin: '4px 0 0 0',
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    margin: '0 0 8px 0',
  },
};

export default Settings;
